import React, { useState, useEffect, useCallback } from 'react';
import { FaHistory, FaChevronUp, FaChevronDown, FaCheck, FaSyncAlt } from 'react-icons/fa';
import { getAuditEntries } from '../services/auditService';

const SOURCE_BADGE_CLASSES = {
  admin: 'bg-blue-100 text-blue-700',
  franchisee: 'bg-green-100 text-green-700',
  supplier: 'bg-purple-100 text-purple-700',
  system: 'bg-gray-100 text-gray-600',
  scheduler: 'bg-gray-100 text-gray-600',
  'xero-webhook': 'bg-indigo-100 text-indigo-700',
  cli: 'bg-gray-100 text-gray-600',
  signup: 'bg-teal-100 text-teal-700',
};

// Actions that represent a notable/terminal outcome get a distinct pill colour; everything else is neutral.
const ACTION_BADGE_CLASSES = {
  created: 'bg-green-100 text-green-700',
  deleted: 'bg-red-100 text-red-700',
  voided: 'bg-red-100 text-red-700',
  'status-changed': 'bg-amber-100 text-amber-700',
  regenerated: 'bg-amber-100 text-amber-700',
  'email-failed': 'bg-red-100 text-red-700',
  'xero-sync-failed': 'bg-red-100 text-red-700',
};
const DEFAULT_ACTION_BADGE_CLASS = 'bg-gray-100 text-gray-600';

const toDate = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp._seconds) return new Date(timestamp._seconds * 1000);
  if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
  const parsed = new Date(timestamp);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const formatWhen = (timestamp) => {
  const date = toDate(timestamp);
  return date
    ? date.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short', hour12: true })
    : 'Unknown time';
};

// "invoicePricePerHour" -> "Invoice Price Per Hour"
const humanizeField = (field) => field.replace(/([A-Z])/g, ' $1').trim();

// "status-changed" -> "Status Changed"
const humanizeAction = (action) => String(action).replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const ChangeRow = ({ change }) => (
  <li className="flex flex-wrap gap-1 items-center">
    <span className="font-medium text-gray-700 capitalize">{humanizeField(change.field)}:</span>
    {change.from !== null && change.from !== undefined && (
      <>
        <span className="line-through text-red-400">{change.from}</span>
        <span className="text-gray-400">→</span>
      </>
    )}
    <span className="text-green-700 font-medium">
      {change.to !== null && change.to !== undefined
        ? change.to
        : <em className="text-gray-400 font-normal">cleared</em>}
    </span>
  </li>
);

const EntryRow = ({ entry }) => (
  <div className="flex gap-3 py-3 border-b border-gray-100">
    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-jims-cyan flex items-center justify-center mt-0.5">
      <FaHistory className="text-white" style={{ fontSize: '10px' }} />
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <span className="text-sm font-medium text-gray-800">{entry.performedBy?.email || 'Unknown'}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SOURCE_BADGE_CLASSES[entry.source] || SOURCE_BADGE_CLASSES.system}`}>
          {entry.source}
        </span>
        {entry.action && (
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ACTION_BADGE_CLASSES[entry.action] || DEFAULT_ACTION_BADGE_CLASS}`}>
            {humanizeAction(entry.action)}
          </span>
        )}
        <span className="text-xs text-gray-400">{formatWhen(entry.timestamp)}</span>
      </div>
      {entry.message && <p className="text-xs text-gray-500 mb-1">{entry.message}</p>}
      {entry.changes && entry.changes.length > 0 && (
        <ul className="text-xs text-gray-600 space-y-0.5 mt-1">
          {entry.changes.map((change, ci) => <ChangeRow key={ci} change={change} />)}
        </ul>
      )}
      {entry.batchId && (
        <span
          className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-400 border border-gray-100"
          title={entry.batchId}
        >
          batch: {entry.batchId.length > 28 ? `${entry.batchId.slice(0, 28)}…` : entry.batchId}
        </span>
      )}
    </div>
  </div>
);

/**
 * Collapsible activity log panel for a record with a `{collection}/{id}/auditLog` subcollection.
 * `createdAt` renders a synthetic "created" entry at the bottom of the timeline, for records
 * predating the audit rollout or entities that don't emit their own `created` entry.
 *
 * Two usage modes (pick one per call site, do not mix):
 * 1. Controlled — pass `entries`/`loading`/`expanded`/`onToggle` yourself (existing job/schedule
 *    pages, which fetch via their own bespoke endpoints).
 * 2. Self-fetching — pass `entityType` + `entityId` and the component fetches from the generic
 *    `/audit/entries` endpoint itself, manages its own loading/expanded/error state.
 */
const ActivityLog = ({
  entries: entriesProp,
  loading: loadingProp,
  expanded: expandedProp,
  onToggle: onToggleProp,
  error: errorProp = null,
  entityType = null,
  entityId = null,
  createdAt = null,
  createdLabel = 'Record created',
  createdDescription = null,
  children,
}) => {
  const selfManaged = Boolean(entityType && entityId);

  const [selfEntries, setSelfEntries] = useState([]);
  const [selfLoading, setSelfLoading] = useState(false);
  const [selfExpanded, setSelfExpanded] = useState(false);
  const [selfError, setSelfError] = useState(null);
  const [selfFetched, setSelfFetched] = useState(false);

  const fetchEntries = useCallback(async () => {
    if (!selfManaged) return;
    setSelfLoading(true);
    setSelfError(null);
    try {
      const res = await getAuditEntries(entityType, entityId);
      // makeAuthenticatedRequest already unwraps the {status, data} envelope; tolerate both shapes.
      setSelfEntries(res?.entries || res?.data?.entries || []);
    } catch (err) {
      console.error(`[ActivityLog] Failed to fetch audit entries for ${entityType}/${entityId}:`, err);
      setSelfError('Could not load activity.');
      setSelfEntries([]);
    } finally {
      setSelfLoading(false);
      setSelfFetched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  // Fetch on mount so the collapsed header can show an accurate entry count — a lazy fetch makes a
  // record with history look identical to one with none until you open the panel.
  useEffect(() => {
    if (selfManaged && !selfFetched) fetchEntries();
  }, [selfManaged, selfFetched, fetchEntries]);

  const entries = selfManaged ? selfEntries : (entriesProp || []);
  const loading = selfManaged ? selfLoading : Boolean(loadingProp);
  const expanded = selfManaged ? selfExpanded : Boolean(expandedProp);
  const onToggle = selfManaged ? () => setSelfExpanded(v => !v) : (onToggleProp || (() => {}));
  const error = selfManaged ? selfError : errorProp;

  return (
    <div className="mt-6 bg-white rounded-lg shadow-md overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-8 py-5 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center">
          <FaHistory className="mr-3 text-jims-cyan-dark text-base" />
          <span className="text-base font-semibold text-gray-700">Activity Log</span>
          {entries.length > 0 && (
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600 font-medium">
              {entries.length} update{entries.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {expanded ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
      </button>

      {expanded && (
        <div className="px-8 pb-6 border-t border-gray-100">
          {children}
          {selfManaged && (
            <button
              type="button"
              onClick={fetchEntries}
              disabled={loading}
              className="mt-4 inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-jims-cyan-dark disabled:opacity-50"
            >
              <FaSyncAlt className={loading ? 'animate-spin' : ''} style={{ fontSize: '10px' }} />
              Refresh
            </button>
          )}
          {error ? (
            <p className="text-red-500 text-sm py-4 text-center">{error}</p>
          ) : loading ? (
            <p className="text-gray-500 text-sm py-4 text-center">Loading activity...</p>
          ) : (
            <div className="mt-4">
              {entries.length === 0 && !createdAt && (
                <p className="text-gray-500 text-sm py-4 text-center">No activity recorded yet.</p>
              )}

              {entries.map((entry) => <EntryRow key={entry.id} entry={entry} />)}

              {/* Synthetic creation entry — covers records predating the audit rollout */}
              {createdAt && (
                <div className="flex gap-3 py-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mt-0.5">
                    <FaCheck className="text-white" style={{ fontSize: '10px' }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-800">{createdLabel}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600">system</span>
                      <span className="text-xs text-gray-400">{formatWhen(createdAt)}</span>
                    </div>
                    {createdDescription && <p className="text-xs text-gray-500">{createdDescription}</p>}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
